import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Code, 
  Key, 
  Shield, 
  Zap, 
  Copy, 
  Check, 
  ExternalLink, 
  BookOpen, 
  Terminal,
  Globe,
  Database,
  Lock,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

const ApiDocumentation = () => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    gettingStarted: true,
    authentication: true,
    links: true,
    profilePages: true,
    examples: true,
    security: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const baseUrl = 'http://localhost:4002/api/v1';

  const codeExamples = {
    createLink: `curl -X POST "${baseUrl}/links" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "originalUrl": "https://example.com",
    "title": "My Link",
    "description": "Link description"
  }'`,
    
    getLinks: `curl -X GET "${baseUrl}/links" \\
  -H "X-API-Key: YOUR_API_KEY"`,
    
    updateLink: `curl -X PUT "${baseUrl}/links/1" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "title": "Updated Title",
    "description": "Updated description"
  }'`,
    
    createProfilePage: `curl -X POST "${baseUrl}/profile-pages" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "name": "My Profile Page",
    "title": "Welcome to My Page",
    "description": "Check out my links!"
  }'`,
    
    javascript: `const API_KEY = 'your_api_key_here';
const BASE_URL = '${baseUrl}';

// Create a link
async function createLink(url, title) {
  const response = await fetch(\`\${BASE_URL}/links\`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    },
    body: JSON.stringify({
      originalUrl: url,
      title: title
    })
  });
  
  return response.json();
}`,
    
    python: `import requests

API_KEY = 'your_api_key_here'
BASE_URL = '${baseUrl}'

headers = {
    'X-API-Key': API_KEY,
    'Content-Type': 'application/json'
}

# Create a link
def create_link(url, title):
    data = {
        'originalUrl': url,
        'title': title
    }
    response = requests.post(f'{BASE_URL}/links', json=data, headers=headers)
    return response.json()`
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-ink-600 shadow-soft">
          <Code className="h-3.5 w-3.5 text-brand-500" /> REST · v1
        </div>
        <h1 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-ink-900">
          API documentation
        </h1>
        <p className="mt-2 text-base text-ink-600 max-w-2xl">
          Create, update, and track links programmatically. Authenticate every
          request with an API key from the{' '}
          <Link to="/api-keys" className="text-brand-600 hover:text-brand-700 font-medium">API keys</Link> page.
        </p>
      </div>

      {/* Quick Navigation */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        {[
          { to: '#getting-started', icon: Zap, label: 'Getting started', accent: 'from-brand-500 to-violet-600' },
          { to: '#authentication', icon: Key, label: 'Authentication', accent: 'from-emerald-500 to-teal-600' },
          { to: '#endpoints', icon: Globe, label: 'Endpoints', accent: 'from-pink-500 to-rose-500' },
        ].map(({ to, icon: Icon, label, accent }) => (
          <Link
            key={to}
            to={to}
            className="group flex items-center gap-3 rounded-2xl border border-ink-200 bg-white p-4 shadow-soft hover:shadow-pop hover:-translate-y-0.5 transition"
          >
            <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white`}>
              <Icon className="h-4 w-4" />
            </span>
            <span className="font-medium text-ink-800 group-hover:text-ink-900">{label}</span>
          </Link>
        ))}
      </div>

      {/* Getting Started */}
      <div id="getting-started" className="bg-white rounded-2xl border border-ink-200 shadow-soft p-8 mb-8">
        <div 
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('gettingStarted')}
        >
          <h2 className="text-2xl font-bold flex items-center">
            <Zap className="h-6 w-6 mr-2 text-blue-600" />
            Getting Started
          </h2>
          {expandedSections.gettingStarted ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>
        
        {expandedSections.gettingStarted && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">Base URL</h3>
                <div className="bg-ink-100 p-3 rounded-lg font-mono text-sm">
                  {baseUrl}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3">Content Type</h3>
                <div className="bg-ink-100 p-3 rounded-lg font-mono text-sm">
                  application/json
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <Info className="h-5 w-5 text-blue-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-blue-800 font-medium">API Key Required</p>
                  <p className="text-blue-700 text-sm">
                    All API requests require an API key. You can manage your API keys in the 
                    <Link to="/api-keys" className="text-blue-600 hover:underline ml-1">API Keys section</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Authentication */}
      <div id="authentication" className="bg-white rounded-2xl border border-ink-200 shadow-soft p-8 mb-8">
        <div 
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('authentication')}
        >
          <h2 className="text-2xl font-bold flex items-center">
            <Key className="h-6 w-6 mr-2 text-green-600" />
            Authentication
          </h2>
          {expandedSections.authentication ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>
        
        {expandedSections.authentication && (
          <div className="space-y-6">
            <p className="text-ink-700">
              All API requests require authentication using an API key. Include your API key in the request header.
            </p>
            
            <div>
              <h3 className="text-lg font-semibold mb-3">Header Format</h3>
              <div className="bg-ink-100 p-3 rounded-lg font-mono text-sm">
                X-API-Key: YOUR_API_KEY_HERE
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">Permission Levels</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <span><strong>links:read</strong> - View links only</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                    <span><strong>links:write</strong> - Create and update links</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                    <span><strong>links:delete</strong> - Delete links</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                    <span><strong>profile:read</strong> - View profile pages</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                    <span><strong>profile:write</strong> - Manage profile pages</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3">Error Responses</h3>
                <div className="space-y-2 text-sm">
                  <div className="bg-red-50 p-3 rounded-lg">
                    <strong>401 Unauthorized</strong> - Invalid or missing API key
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <strong>403 Forbidden</strong> - Insufficient permissions
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg">
                    <strong>404 Not Found</strong> - Resource not found
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* API Endpoints */}
      <div id="endpoints" className="space-y-8">
        {/* Links Endpoints */}
        <div className="bg-white rounded-2xl border border-ink-200 shadow-soft p-8">
          <div 
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => toggleSection('links')}
          >
            <h2 className="text-2xl font-bold flex items-center">
              <Globe className="h-6 w-6 mr-2 text-purple-600" />
              Link Management Endpoints
            </h2>
            {expandedSections.links ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </div>
          
          {expandedSections.links && (
            <div className="space-y-6">
              {/* Create Link */}
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="text-lg font-semibold mb-2">POST /links - Create Link</h3>
                <p className="text-ink-600 mb-3">Create a new dynamic link</p>
                <div className="bg-ink-100 p-4 rounded-lg mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-mono text-sm">Required Permissions: links:write</span>
                    <button 
                      onClick={() => copyToClipboard(codeExamples.createLink, 'createLink')}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'createLink' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'createLink' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-sm overflow-x-auto">{codeExamples.createLink}</pre>
                </div>
              </div>

              {/* Get Links */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-lg font-semibold mb-2">GET /links - Get All Links</h3>
                <p className="text-ink-600 mb-3">Retrieve all links for the authenticated user</p>
                <div className="bg-ink-100 p-4 rounded-lg mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-mono text-sm">Required Permissions: links:read</span>
                    <button 
                      onClick={() => copyToClipboard(codeExamples.getLinks, 'getLinks')}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'getLinks' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'getLinks' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-sm overflow-x-auto">{codeExamples.getLinks}</pre>
                </div>
              </div>

              {/* Update Link */}
              <div className="border-l-4 border-yellow-500 pl-4">
                <h3 className="text-lg font-semibold mb-2">PUT /links/:id - Update Link</h3>
                <p className="text-ink-600 mb-3">Update an existing link</p>
                <div className="bg-ink-100 p-4 rounded-lg mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-mono text-sm">Required Permissions: links:write</span>
                    <button 
                      onClick={() => copyToClipboard(codeExamples.updateLink, 'updateLink')}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'updateLink' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'updateLink' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-sm overflow-x-auto">{codeExamples.updateLink}</pre>
                </div>
              </div>

              {/* Other Link Endpoints */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">GET /links/:id</h4>
                  <p className="text-sm text-ink-600 mb-2">Get specific link</p>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">links:read</span>
                </div>
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">DELETE /links/:id</h4>
                  <p className="text-sm text-ink-600 mb-2">Delete a link</p>
                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">links:delete</span>
                </div>
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">GET /links/:id/analytics</h4>
                  <p className="text-sm text-ink-600 mb-2">Get link analytics</p>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">links:read</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Profile Pages Endpoints */}
        <div className="bg-white rounded-2xl border border-ink-200 shadow-soft p-8">
          <div 
            className="flex items-center justify-between cursor-pointer mb-4"
            onClick={() => toggleSection('profilePages')}
          >
            <h2 className="text-2xl font-bold flex items-center">
              <Database className="h-6 w-6 mr-2 text-indigo-600" />
              Profile Page Management Endpoints
            </h2>
            {expandedSections.profilePages ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </div>
          
          {expandedSections.profilePages && (
            <div className="space-y-6">
              {/* Create Profile Page */}
              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="text-lg font-semibold mb-2">POST /profile-pages - Create Profile Page</h3>
                <p className="text-ink-600 mb-3">Create a new profile page</p>
                <div className="bg-ink-100 p-4 rounded-lg mb-3">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-mono text-sm">Required Permissions: profile:write</span>
                    <button 
                      onClick={() => copyToClipboard(codeExamples.createProfilePage, 'createProfilePage')}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'createProfilePage' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'createProfilePage' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-sm overflow-x-auto">{codeExamples.createProfilePage}</pre>
                </div>
              </div>

              {/* Profile Page Endpoints Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">GET /profile-pages</h4>
                  <p className="text-sm text-ink-600 mb-2">Get all profile pages</p>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">profile:read</span>
                </div>
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">GET /profile-pages/:id</h4>
                  <p className="text-sm text-ink-600 mb-2">Get specific profile page</p>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">profile:read</span>
                </div>
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">PUT /profile-pages/:id</h4>
                  <p className="text-sm text-ink-600 mb-2">Update profile page</p>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">profile:write</span>
                </div>
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">DELETE /profile-pages/:id</h4>
                  <p className="text-sm text-ink-600 mb-2">Delete profile page</p>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">profile:write</span>
                </div>
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">POST /profile-pages/:id/links</h4>
                  <p className="text-sm text-ink-600 mb-2">Add link to profile page</p>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">profile:write</span>
                </div>
                <div className="border border-ink-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">GET /profile-pages/:id/analytics</h4>
                  <p className="text-sm text-ink-600 mb-2">Get profile page analytics</p>
                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">profile:read</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Code Examples */}
      <div className="bg-white rounded-2xl border border-ink-200 shadow-soft p-8 mb-8">
        <div 
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('examples')}
        >
          <h2 className="text-2xl font-bold flex items-center">
            <Terminal className="h-6 w-6 mr-2 text-orange-600" />
            Code Examples
          </h2>
          {expandedSections.examples ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>
        
        {expandedSections.examples && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* JavaScript Example */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm mr-2">JavaScript</span>
                  JavaScript/Node.js
                </h3>
                <div className="bg-ink-100 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-mono text-sm">Example</span>
                    <button 
                      onClick={() => copyToClipboard(codeExamples.javascript, 'javascript')}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'javascript' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'javascript' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-sm overflow-x-auto">{codeExamples.javascript}</pre>
                </div>
              </div>

              {/* Python Example */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm mr-2">Python</span>
                  Python
                </h3>
                <div className="bg-ink-100 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-mono text-sm">Example</span>
                    <button 
                      onClick={() => copyToClipboard(codeExamples.python, 'python')}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      {copiedCode === 'python' ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                      {copiedCode === 'python' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <pre className="text-sm overflow-x-auto">{codeExamples.python}</pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Security Best Practices */}
      <div className="bg-white rounded-2xl border border-ink-200 shadow-soft p-8 mb-8">
        <div 
          className="flex items-center justify-between cursor-pointer mb-4"
          onClick={() => toggleSection('security')}
        >
          <h2 className="text-2xl font-bold flex items-center">
            <Shield className="h-6 w-6 mr-2 text-red-600" />
            Security Best Practices
          </h2>
          {expandedSections.security ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>
        
        {expandedSections.security && (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Lock className="h-5 w-5 mr-2 text-red-600" />
                  API Key Security
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2 mt-2"></div>
                    <span>Never commit API keys to version control</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2 mt-2"></div>
                    <span>Store keys securely (environment variables, secure vaults)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2 mt-2"></div>
                    <span>Use different keys for different environments</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-2 mt-2"></div>
                    <span>Rotate keys regularly</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
                  Permission Principle
                </h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2 mt-2"></div>
                    <span>Use minimal required permissions for each API key</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2 mt-2"></div>
                    <span>Create separate keys for different applications</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2 mt-2"></div>
                    <span>Review and revoke unused keys regularly</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2 mt-2"></div>
                    <span>Monitor API key usage through the dashboard</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-yellow-800 font-medium">Important Security Note</p>
                  <p className="text-yellow-700 text-sm">
                    API keys cannot be recovered once revoked. Always keep a backup of your keys and update your applications when rotating keys.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Call to Action */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-md p-8 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
        <p className="text-xl mb-6 opacity-90">
          Create your first API key and start integrating Dynamic Links into your applications.
        </p>
        <div className="flex items-center justify-center space-x-4">
          <Link
            to="/api-keys"
            className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-ink-100 transition-colors"
          >
            <Key className="h-5 w-5 mr-2" />
            Manage API Keys
          </Link>
          <Link
            to="/create"
            className="inline-flex items-center px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-blue-600 transition-colors"
          >
            <ExternalLink className="h-5 w-5 mr-2" />
            Create Your First Link
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
